import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { reportGoalIntervals, reportTeamMetrics } from '../../utils/rivalReport';
import ReportInsights from './ReportInsights';

export function ReportSection({ active, section, children }) {
  return active === section || active === 'all' ? <div className="rr-detail-section">{children}</div> : null;
}

export function ReportSnapshot({ statistics, standing, name }) {
  const { t, i18n } = useTranslation('reports');
  const metrics = reportTeamMetrics(statistics, standing);
  const format = value => value === null ? '—' : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  return <section className="rr-snapshot" aria-label={t('sections.executiveSummary', { team: name })}>
    <div className="rr-snapshot-label"><span className="rr-eyebrow">{t('modern.analyzing')}</span><h2>{name}</h2>
      <p>{metrics.played === null ? t('modern.sampleUnknown') : t('modern.sample', { count: metrics.played })}</p></div>
    <dl className="rr-kpis">
      {[
        ['stats.position', metrics.position && metrics.position > 0 ? `${format(metrics.position)}º` : '—'],
        ['stats.points', format(metrics.points)],
        ['modern.pointsRate', format(metrics.pointsRate)],
        ['modern.goalBalance', `${format(metrics.goalsFor)} / ${format(metrics.goalsAgainst)}`],
      ].map(([key, value]) => <div key={key}><dt>{t(key)}</dt><dd>{value}</dd></div>)}
    </dl>
  </section>;
}

export function ReportGoalsChart({ statistics, standing, scope = 'total', missingValue = '—' }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const title = t(scope === 'total' ? 'modern.goalTiming' : `modern.goals.${scope}Timing`);
  const intervals = reportGoalIntervals(statistics, standing).map(row => ({
    ...row,
    forGoals: scope === 'total' ? row.forGoals : row[`${scope}For`],
    againstGoals: scope === 'total' ? row.againstGoals : row[`${scope}Against`],
  }));
  const maximum = Math.max(1, ...intervals.flatMap(row => [row.forGoals ?? 0, row.againstGoals ?? 0]));
  const available = intervals.some(row => row.forGoals !== null || row.againstGoals !== null);
  const format = value => value === null ? missingValue : value.toLocaleString(i18n.language);
  return <section className="rr-panel rr-goal-timing" aria-labelledby={titleId}>
    <header><span className="rr-eyebrow">{t('modern.goalEyebrow')}</span><h3 id={titleId}>{title}</h3>
      <p>{t('modern.goalTimingHelp')}</p></header>
    {available ? <>
      <div className="rr-chart-legend"><span><i className="rr-swatch" />{t('stats.goalsFor')}</span>
        <span><i className="rr-swatch rr-swatch-against" />{t('stats.goalsAgainst')}</span></div>
      <div className="rr-goal-chart" aria-hidden="true">
        {intervals.map(row => <div className="rr-goal-column" key={row.interval}>
          <div className="rr-goal-bars">
            {[row.forGoals, row.againstGoals].map((value, index) => <div key={index} className={`rr-goal-bar rr-goal-bar-${index}`}
              style={{ height: `${(value ?? 0) / maximum * 100}%` }}><span>{format(value)}</span></div>)}
          </div><span>{row.label}</span>
        </div>)}
      </div>
    </> : <p className="rr-empty">{t('modern.intervalUnavailable')}</p>}
    <div className="rr-data-table"><table aria-label={title}>
      <thead><tr><th>{t('modern.interval')}</th><th>{t('stats.goalsFor')}</th><th>{t('stats.goalsAgainst')}</th></tr></thead>
      <tbody>{intervals.map(row => <tr key={row.interval}><th scope="row">{row.label}</th>
        <td>{format(row.forGoals)}</td><td>{format(row.againstGoals)}</td></tr>)}</tbody>
    </table></div>
  </section>;
}

export default function RivalReportOverview({ statistics, standing, otherStatistics, otherStanding,
  name, otherName, recent, players, home, onSection }) {
  const { t, i18n } = useTranslation('reports');
  const metrics = reportTeamMetrics(statistics, standing);
  const other = reportTeamMetrics(otherStatistics, otherStanding);
  const format = value => value === null ? '—' : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const lastFive = recent.slice(-5);
  const recentPoints = lastFive.reduce((sum, match) => sum + match.points, 0);
  const comparisons = [
    ['modern.pointsRate', metrics.pointsRate, other.pointsRate],
    ['modern.goalsForRate', metrics.goalsForRate, other.goalsForRate],
    ['modern.goalsAgainstRate', metrics.goalsAgainstRate, other.goalsAgainstRate],
    ['modern.winRate', metrics.winRate, other.winRate],
  ];
  return <>
    <div className="rr-overview-grid">
      <section className="rr-panel rr-brief" aria-labelledby="rr-brief-title">
        <header><span className="rr-eyebrow">{t('modern.quickRead')}</span><h2 id="rr-brief-title">{t('modern.matchPreparation')}</h2></header>
        <dl>
          <div><dt>{t('modern.recentPoints')}</dt><dd>{lastFive.length ? `${recentPoints} / ${lastFive.length * 3}` : '—'}</dd>
            <p>{lastFive.length ? t('modern.recentSample', { count: lastFive.length }) : t('modern.noVerifiedResults')}</p></div>
          <div><dt>{t('modern.matchCondition')}</dt><dd>{t(home ? 'match.home' : 'match.away')}</dd><p>{name}</p></div>
          <div><dt>{t('modern.scoringBalance')}</dt><dd>{format(metrics.goalsForRate)} / {format(metrics.goalsAgainstRate)}</dd>
            <p>{t('modern.balancePerMatch')}</p></div>
        </dl>
        <button type="button" onClick={() => onSection('all')}>{t('modern.seeEverything')}<ArrowForwardIcon fontSize="small" /></button>
      </section>
      <section className="rr-panel" aria-labelledby="rr-comparison-title">
        <header><span className="rr-eyebrow">{t('modern.sameUnits')}</span><h2 id="rr-comparison-title">{t('modern.headToHead')}</h2>
          <p>{t('modern.comparisonHelp')}</p></header>
        <div className="rr-comparison-names"><strong>{name}<small>{metrics.played === null ? '—' : t('modern.sample', { count: metrics.played })}</small></strong>
          <strong>{otherName}<small>{other.played === null ? '—' : t('modern.sample', { count: other.played })}</small></strong></div>
        <div className="rr-comparison-list">
          {comparisons.map(([key, selected, opponent]) => <div key={key}>
            <span>{format(selected)}{key === 'modern.winRate' && selected !== null ? '%' : ''}</span>
            <p>{t(key)}</p><span>{format(opponent)}{key === 'modern.winRate' && opponent !== null ? '%' : ''}</span>
          </div>)}
        </div>
        <button className="rr-text-action" type="button" onClick={() => onSection('comparison')}>{t('modern.openComparison')}<ArrowForwardIcon fontSize="small" /></button>
      </section>
    </div>
    <ReportInsights statistics={statistics} standing={standing} recent={recent} players={players} />
    <section className="rr-panel" aria-labelledby="rr-form-title">
      <header className="rr-panel-heading"><div><span className="rr-eyebrow">{t('modern.recentEyebrow')}</span>
        <h2 id="rr-form-title">{t('modern.formTitle')}</h2><p>{t('modern.formHelp')}</p></div><TrendingUpIcon aria-hidden="true" /></header>
      {recent.length ? <>
        <div className="rr-form-chart" aria-hidden="true">
          {recent.map((match, index) => <div key={`${match.round}-${index}`} className={`rr-form-column rr-outcome-${match.outcome}`}>
            <strong>{match.points}</strong><div style={{ height: `${match.points * 30}px` }} />
            <b>{t(`modern.outcomes.${match.outcome}`)}</b><span>{t('modern.roundShort', { n: match.round ?? '—' })}</span></div>)}
        </div>
        <div className="rr-data-table"><table aria-label={t('modern.formTitle')}>
          <thead><tr><th>{t('modern.round')}</th><th>{t('modern.opponent')}</th><th>{t('modern.condition')}</th>
            <th>{t('modern.result')}</th><th>{t('stats.points')}</th></tr></thead>
          <tbody>{recent.map((match, index) => <tr key={`${match.round}-${index}`}><td>{match.round ?? '—'}</td><th scope="row">{match.opponent}</th>
            <td>{t(match.home ? 'match.home' : 'match.away')}</td><td><span className={`rr-result rr-outcome-${match.outcome}`}>
              {t(`modern.outcomes.${match.outcome}`)}</span> {match.forGoals} - {match.againstGoals}</td><td>{match.points}</td></tr>)}</tbody>
        </table></div>
      </> : <p className="rr-empty">{t('modern.noVerifiedResults')}</p>}
      <button className="rr-text-action" type="button" onClick={() => onSection('performance')}>{t('modern.openPerformance')}<ArrowForwardIcon fontSize="small" /></button>
    </section>
    <section className="rr-detail-index" aria-label={t('modern.detailIndex')}>
      {['performance', 'goals', 'players', 'discipline', 'comparison'].map(section => <button type="button" key={section} onClick={() => onSection(section)}>
        <strong>{t(`modern.tabs.${section}`)}</strong><span>{t(`modern.descriptions.${section}`)}</span><ArrowForwardIcon fontSize="small" />
      </button>)}
    </section>
  </>;
}
